import Hls from 'hls.js';

/*
 * Grabs one still frame from an HLS stream and returns it as a small JPEG data URL.
 * Uses the lightest rendition, needs only the first segment, runs at most two grabs at a time,
 * caches one result per stream for the session, and gives up after a timeout.
 */

const FRAME_AT_SECONDS = 2;
const FRAME_WIDTH = 480;
const MAX_PARALLEL = 2;
const TIMEOUT_MS = 15000;

const cache = new Map<string, Promise<string | null>>();
const waiting: (() => void)[] = [];
let running = 0;

export function captureVideoFrame(streamUrl: string): Promise<string | null> {
  if (!cache.has(streamUrl)) cache.set(streamUrl, schedule(() => grabFrame(streamUrl)));
  return cache.get(streamUrl) as Promise<string | null>;
}

function schedule(job: () => Promise<string | null>): Promise<string | null> {
  return new Promise(resolve => {
    const run = () => {
      running++;
      job().catch(() => null).then(result => {
        running--;
        resolve(result);
        waiting.shift()?.();
      });
    };
    if (running < MAX_PARALLEL) run(); else waiting.push(run);
  });
}

function grabFrame(streamUrl: string): Promise<string | null> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    let hls: Hls | null = null;
    let finished = false;
    const finish = (dataUrl: string | null) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      hls?.destroy();
      video.removeAttribute('src');
      video.load();
      resolve(dataUrl);
    };
    const timer = setTimeout(() => finish(null), TIMEOUT_MS);

    video.addEventListener('loadedmetadata', () => {
      const duration = isFinite(video.duration) ? video.duration : FRAME_AT_SECONDS * 2;
      video.currentTime = Math.min(FRAME_AT_SECONDS, duration / 2);
    }, { once: true });
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = FRAME_WIDTH;
        canvas.height = Math.round(FRAME_WIDTH * (video.videoHeight || 9) / (video.videoWidth || 16));
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg', 0.8));
      } catch {
        finish(null);
      }
    }, { once: true });
    video.addEventListener('error', () => finish(null), { once: true });

    if (Hls.isSupported()) {
      hls = new Hls({ startLevel: 0, maxBufferLength: 4, maxMaxBufferLength: 6 });
      hls.on(Hls.Events.ERROR, (_event, data) => { if (data.fatal) finish(null); });
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
    } else {
      finish(null);
    }
  });
}
